import Navigation from "@/components/Navigation";
import { Users2, Clock, CheckCircle, XCircle, Send, Ban } from "lucide-react";
import { useCircleApplication } from "@/hooks/useCircleApplication";
import { useCircleConfig } from "@/hooks/useCircleConfig";
import { useCircleBanStatus } from "@/hooks/useCircleBanStatus";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { CircleEventsList } from "@/components/circle/CircleEventsList";
import { CircleEventFormDialog } from "@/components/circle/CircleEventFormDialog";
import { useState } from "react";

const Circle = () => {
  const { application, isLoading, submitApplication, hasApplied, isPending, isApproved, isRejected } = useCircleApplication();
  const { config, isLoading: isConfigLoading } = useCircleConfig();
  const { isBanned, banReason, isLoading: isBanLoading } = useCircleBanStatus();
  const [message, setMessage] = useState("");

  const handleSubmit = () => {
    submitApplication.mutate(message || undefined);
    setMessage("");
  };

  if (isLoading || isConfigLoading || isBanLoading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="p-6 space-y-6">
        {/* Header with + button */}
        <div className="relative">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-violet-500/10 mb-4">
              <Users2 className="w-8 h-8 text-violet-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Circle</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              Community-led experiences created by members like you. 
              Join gallery visits, dinners, and more.
            </p>
          </div>
          {isApproved && !isBanned && (
            <div className="absolute top-0 right-0">
              <CircleEventFormDialog />
            </div>
          )}
        </div>

        {/* Banned State */}
        {isBanned && (
          <Card className="border-destructive/20 bg-destructive/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Ban className="w-5 h-5 text-destructive" />
                <CardTitle className="text-lg text-destructive">Access Suspended</CardTitle>
              </div>
              <CardDescription>
                Your access to Circle has been suspended and you cannot attend Circle events.
                {banReason && <span className="block mt-2">Reason: {banReason}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">
                If you believe this is a mistake, please contact our support team.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Events List - visible to all logged-in users */}
        {!isBanned && (
          <div className="space-y-6">
            <CircleEventsList isCircleMember={isApproved} />
          </div>
        )}

        {/* Pending State */}
        {isPending && (
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-lg text-amber-500">Application Under Review</CardTitle>
              </div>
              <CardDescription>
                We're reviewing your application. You'll be notified once it's approved.
              </CardDescription>
            </CardHeader>
            {application?.message && (
              <CardContent>
                <div className="text-sm">
                  <span className="text-muted-foreground">Your message: </span>
                  <span className="text-foreground">{application.message}</span>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Rejected State */}
        {isRejected && (
          <Card className="border-red-500/20 bg-red-500/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <CardTitle className="text-lg text-red-500">Application Not Approved</CardTitle>
              </div>
              <CardDescription>
                Unfortunately, your application wasn't approved at this time.
                {application?.rejection_reason && (
                  <span className="block mt-2">Reason: {application.rejection_reason}</span>
                )}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Application Form */}
        {!hasApplied && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Apply to Join Circle</CardTitle>
              <CardDescription>
                Circle is our community-driven event program where members can host and join casual gatherings.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Tell us why you'd like to join Circle"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                required
              />
              <Button 
                onClick={handleSubmit}
                disabled={submitApplication.isPending || !message.trim()}
                className="w-full bg-violet-500 hover:bg-violet-600"
              >
                {submitApplication.isPending ? <LoadingSpinner className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Apply to Join
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <Navigation />
    </div>
  );
};

export default Circle;
